import React, { useState, useEffect, useRef } from 'react';
import { getWebSocketUrl, getApiUrl } from '../../config/websocket';
import './TextChat.scss';

/**
 * Текстовый чат с WebSocket для комнат сообщества
 */
const TextChat = ({ roomSlug, currentUser }) => {
  const [ws, setWs] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    console.log('TextChat useEffect - roomSlug:', roomSlug, 'currentUser:', currentUser);
    
    if (!roomSlug || !currentUser) {
      console.warn('TextChat: Missing roomSlug or currentUser', { roomSlug, currentUser });
      return;
    }

    // Загружаем историю сообщений
    const loadHistory = async () => {
      setIsLoading(true);
      try {
        const token = localStorage.getItem('token') || localStorage.getItem('access_token');
        const apiUrl = getApiUrl(`/api/community/chat-rooms/${roomSlug}/messages/`);
        console.log('Fetching messages from:', apiUrl);
        
        const response = await fetch(apiUrl, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          setMessages(data.reverse());
        }
      } catch (error) {
        console.error('Failed to load message history:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadHistory();

    // Подключение к WebSocket
    const wsUrl = getWebSocketUrl(`ws/chat/${roomSlug}/`);
    console.log('Connecting to WebSocket:', wsUrl);
    
    const websocket = new WebSocket(wsUrl);

    websocket.onopen = () => {
      console.log('✅ Text chat connected successfully');
      setIsConnected(true);
    };

    websocket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.error) {
        console.error('WebSocket error:', data.error);
        return;
      }
      addMessage(data);
    };

    websocket.onclose = () => {
      console.log('Text chat disconnected');
      setIsConnected(false);
    };

    websocket.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    setWs(websocket);

    return () => {
      if (websocket.readyState === WebSocket.OPEN) {
        websocket.close();
      }
    };
  }, [roomSlug, currentUser]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const addMessage = (message) => {
    setMessages(prev => [...prev, {
      id: message.id || Date.now(),
      user: message.user,
      content: message.content,
      created_at: message.created_at || new Date().toISOString(),
      isOptimistic: false
    }]);
  };

  const sendMessage = () => {
    if (!inputValue.trim() || !ws || ws.readyState !== WebSocket.OPEN) return;

    // Optimistic update
    const tempMessage = {
      id: `temp-${Date.now()}`,
      user: currentUser.username,
      content: inputValue,
      created_at: new Date().toISOString(),
      isOptimistic: true
    };
    setMessages(prev => [...prev, tempMessage]);

    // Отправляем на сервер
    ws.send(JSON.stringify({
      message: inputValue
    }));

    setInputValue('');
    inputRef.current?.focus();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;

    if (diff < 60000) return 'только что';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} мин назад`;
    if (diff < 86400000) return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
    return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="text-chat">
      <div className="text-chat__header">
        <h3>Чат</h3>
        <div className={`text-chat__status ${isConnected ? 'connected' : 'disconnected'}`}>
          {isConnected ? 'Онлайн' : 'Офлайн'}
        </div>
      </div>

      <div className="text-chat__messages">
        {isLoading ? (
          <div className="text-chat__loading">Загрузка сообщений...</div>
        ) : messages.length === 0 ? (
          <div className="text-chat__empty">
            <p>Пока нет сообщений</p>
            <p>Будьте первым, кто начнёт разговор!</p>
          </div>
        ) : (
          <>
            {messages.map((msg, index) => {
              const isOwn = msg.user === currentUser?.username;
              const showAvatar = index === 0 || messages[index - 1].user !== msg.user;

              const avatarUrl = msg.user_avatar 
                ? (msg.user_avatar.startsWith('http') ? msg.user_avatar : `${window.location.origin}${msg.user_avatar}`)
                : `https://ui-avatars.com/api/?name=${encodeURIComponent(msg.user || 'User')}&background=E91E63&color=fff&bold=true`
              
              return (
                <div
                  key={msg.id}
                  className={`text-chat__message ${isOwn ? 'own' : ''} ${msg.isOptimistic ? 'optimistic' : ''}`}
                >
                  {showAvatar && !isOwn && (
                    <img 
                      src={avatarUrl}
                      alt={msg.user}
                      className="text-chat__avatar"
                      onError={(e) => {
                        e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(msg.user || 'User')}&background=E91E63&color=fff&bold=true`
                      }}
                    />
                  )}
                  <div className="text-chat__message-content">
                    {showAvatar && (
                      <div className="text-chat__message-header">
                        <span className="text-chat__username">{msg.user}</span>
                        <span className="text-chat__timestamp">{formatTime(msg.created_at)}</span>
                      </div>
                    )}
                    <div className="text-chat__message-text">{msg.content}</div>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      <div className="text-chat__input-container">
        <textarea
          ref={inputRef}
          className="text-chat__input"
          placeholder="Написать сообщение..."
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={!isConnected}
          rows={1}
        />
        <button
          className="text-chat__send-btn"
          onClick={sendMessage}
          disabled={!inputValue.trim() || !isConnected}
        >
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default TextChat;
